// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// What a catalog checkout says pays for the order (EXP-20261020-70, -75). The rule is the server's (saveCatalogOrder's
// tier gate): an account billed on the Standard tier is charged per order on its default card, the total without the
// license fee (which is invoiced) plus the sales tax on that charge; an account on a committed tier is invoiced against
// its commitment and nothing goes on its card. A catalog order never draws on a prepaid credit balance.
//
// Claude's plain wording (Joel, 2026-10-20); Janan may revise it here. The order form's price block, Billing and
// Payment, the estimate and invoice PDF and the e2e tests (e2e/tests/test_catalog_checkout_wording.py reads this
// object, so each entry stays one plain string literal: no concatenation, no ${}) all print these words. {name} is
// filled in.
export const CATALOG_BILLING_COPY = {
  invoiced: 'Invoiced against your committed tier. Nothing is charged to your card.',
  cardCharge: '{amount} will be charged to {card} when you submit this order.',
  cardEnding: 'your card ending {last4}',
  cardUnnamed: 'your card',
  // an order held for an imagery request (Submit saves it; it is placed, and charged, once the imagery is in) and an
  // estimate PDF
  cardAtPlacement: 'Charged to {card} when the order is placed.',
  cardCharged: 'Charged to your card.',
  // the added license fee beside a card charge: it is never charged to a card (AUT-6569 D4)
  licenseFeeInvoiced: 'invoiced separately',
  listRate: 'Catalog list rate',
  tierRate: 'Catalog {tier} tier rate',
  rateNotFromBalance: '{rate} · not from your prepaid credits',
};

const fill = (template: string, values: Record<string, string>): string =>
  template.replace(/\{(\w+)\}/g, (placeholder, name: string) => values[name] ?? placeholder);

export type CatalogBilling = 'card' | 'invoiced';

// The tier the account is billed on decides, as the server's gate reads it (the quote's committedTierName); unknown
// until the quote names it.
export const catalogBilling = (committedTierName?: string | null): CatalogBilling | null => {
  if (!committedTierName) return null;
  return committedTierName === 'Standard' ? 'card' : 'invoiced';
};

// Whether the account holds prepaid credits the catalog credits could be read as: every plan but pay-as-you-go, and an
// account with no plan that carries a balance (a Stripe subscription).
export const hasPrepaidCredits = (pricingPlanName?: string | null, availableCreditAcreage?: number | null): boolean =>
  (pricingPlanName ? pricingPlanName !== 'no_commit' : (availableCreditAcreage ?? 0) > 0);

const cardText = (last4?: string | null): string => (last4 ? fill(CATALOG_BILLING_COPY.cardEnding, { last4 }) : CATALOG_BILLING_COPY.cardUnnamed);

// The note under a card-charged Total: the amount and the default card it goes on.
export const catalogCardChargeNote = (amountText: string, last4?: string | null): string =>
  fill(CATALOG_BILLING_COPY.cardCharge, { amount: amountText, card: cardText(last4) });

// The same order held for its imagery request: SUBMIT saves it, and the card is charged when it is placed, at a total
// confirmed then.
export const catalogHeldCardNote = (last4?: string | null): string => fill(CATALOG_BILLING_COPY.cardAtPlacement, { card: cardText(last4) });

// The caption under the catalog credits row: whose rate prices them and, on an account with prepaid credits, that the
// order does not spend those.
export const catalogCreditsCaption = (tierName: string | null | undefined, prepaidCredits: boolean): string => {
  const rate = !tierName || tierName === 'Standard' ? CATALOG_BILLING_COPY.listRate : fill(CATALOG_BILLING_COPY.tierRate, { tier: tierName });
  return prepaidCredits ? fill(CATALOG_BILLING_COPY.rateNotFromBalance, { rate }) : rate;
};

interface CatalogPdfBillingInputs {
  type: string;
  tierName?: string | null;
  committedTierName?: string | null;
  totalAdditionalCharged?: number;
  requiresManualQuote?: boolean;
}

// The catalog PDF's note. A card amount on the record was (or will be) charged to the card. With nothing on the card,
// the tier the account is billed on decides: the form's estimate names it (committedTierName, beside the cart's own
// tier), the server's PDF data names it as its tier. An order records no billing tier of its own, so an invoice that
// put nothing on the card of a Standard account (a $0 override) says nothing rather than claim an invoice.
export const catalogPdfBillingNote = ({ type, tierName, committedTierName, totalAdditionalCharged, requiresManualQuote }: CatalogPdfBillingInputs): string | null => {
  if (requiresManualQuote || typeof totalAdditionalCharged !== 'number') return null;
  const invoice = type === 'Invoice';
  if (totalAdditionalCharged > 0) return invoice ? CATALOG_BILLING_COPY.cardCharged : catalogHeldCardNote(null);
  const billing = catalogBilling(committedTierName || tierName);
  if (billing === 'invoiced') return CATALOG_BILLING_COPY.invoiced;
  return billing === 'card' && !invoice ? catalogHeldCardNote(null) : null;
};
